import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useBookingApi } from './bookingApi';
import BookingDetailModal from './BookingDetailModal';
import {
    BOOKING_STATUSES,
    BOOKING_STATUS_LABELS,
    BOOKING_STATUS_TONE,
    formatTimeOnly,
    formatDateOnly,
    formatInterval,
    formatCents,
    bookingCustomerLabel,
} from './bookingFormat';

/**
 * The booking agenda — a filterable list of reservations across all of the
 * seller's calendars, with the pending-approval queue surfaced up top. Rows
 * open BookingDetailModal for the lifecycle actions. Semi-independent: pass an
 * optional `resources` list to power the calendar filter; it fetches bookings
 * itself.
 */
const SCOPES = [
    { key: 'upcoming', label: 'Upcoming' },
    { key: 'past', label: 'Past' },
    { key: 'all', label: 'All' },
];

export default function BookingAgenda({ resources = [] }) {
    const api = useBookingApi();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [scope, setScope] = useState('upcoming');
    const [status, setStatus] = useState('all');
    const [resourceId, setResourceId] = useState('');
    const [selected, setSelected] = useState(null);
    const [holdCount, setHoldCount] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        setHoldCount(0);
        try {
            const filters = {};
            const nowIso = new Date().toISOString();
            if (scope === 'upcoming') filters.from = nowIso;
            if (scope === 'past') filters.to = nowIso;
            if (status) filters.status = status;
            if (resourceId) filters.resourceId = resourceId;
            const response = await api.listBookings(filters);
            setBookings(response.data || []);
            setHoldCount(response.meta?.holdCount || 0);
        } catch (err) {
            setError(err.message || 'Could not load bookings.');
        } finally {
            setLoading(false);
        }
    }, [api, scope, status, resourceId]);

    useEffect(() => { load(); }, [load]);

    // Merge lifecycle results back in while preserving list-only fields
    // (resource/product includes the action endpoints don't return).
    const handleUpdated = (updated) => {
        if (!updated) { load(); return; }
        setBookings(prev => prev.map(b => (
            b.id === updated.id ? { ...b, ...updated, resource: b.resource, product: b.product } : b
        )));
    };

    const pendingCount = useMemo(
        () => bookings.filter(b => b.status === 'pending').length,
        [bookings],
    );

    // Group by calendar day (in each booking's display timezone).
    const groups = useMemo(() => {
        const map = new Map();
        bookings.forEach(booking => {
            const tz = booking.timezone || booking.resource?.timezone;
            const key = formatDateOnly(booking.startAt, tz);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(booking);
        });
        return Array.from(map.entries());
    }, [bookings]);

    return (
        <div className="booking-agenda">
            <div className="booking-pane-header">
                <div>
                    <h3>Agenda</h3>
                    <p className="help">Reservations across all calendars. Tap one to approve, decline, or cancel.</p>
                </div>
                <button type="button" className="button secondary small" onClick={load} disabled={loading}>
                    <i className="fas fa-sync-alt" aria-hidden="true"></i>
                    <span className="booking-btn-label"> Refresh</span>
                </button>
            </div>

            {pendingCount > 0 && (
                <div className="booking-pending-banner">
                    <i className="fas fa-bell" aria-hidden="true"></i>
                    {pendingCount} booking{pendingCount === 1 ? '' : 's'} awaiting your approval.
                    <button type="button" className="booking-linkbtn" onClick={() => setStatus('pending')}>
                        Review
                    </button>
                </div>
            )}

            <div className="booking-agenda-filters">
                <div className="booking-scope" role="tablist">
                    {SCOPES.map(s => (
                        <button
                            key={s.key}
                            type="button"
                            role="tab"
                            aria-selected={scope === s.key}
                            className={`booking-scope-item${scope === s.key ? ' is-active' : ''}`}
                            onClick={() => setScope(s.key)}
                        >
                            {s.label}
                        </button>
                    ))}
                </div>
                <select value={status} onChange={e => setStatus(e.target.value)} aria-label="Status filter">
                    <option value="all">All statuses</option>
                    {BOOKING_STATUSES.filter(s => s !== 'hold').map(s => (
                        <option key={s} value={s}>{BOOKING_STATUS_LABELS[s]}</option>
                    ))}
                    <option value="hold">On hold</option>
                </select>
                {holdCount > 0 && status !== 'hold' && (
                    <span className="booking-badge muted booking-hold-count">
                        {holdCount} on hold
                    </span>
                )}
                {resources.length > 0 && (
                    <select value={resourceId} onChange={e => setResourceId(e.target.value)} aria-label="Calendar filter">
                        <option value="">All calendars</option>
                        {resources.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                )}
            </div>

            {loading ? (
                <div className="booking-state booking-state--loading">
                    <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    <span>Loading bookings…</span>
                </div>
            ) : error ? (
                <div className="booking-state booking-state--error" role="alert">
                    <div className="error">{error}</div>
                    <button type="button" className="button secondary" onClick={load}>Try again</button>
                </div>
            ) : bookings.length === 0 ? (
                <div className="booking-empty">
                    <i className="fas fa-calendar-check" aria-hidden="true"></i>
                    <h4>No bookings here</h4>
                    <p>Nothing matches the current filters.</p>
                </div>
            ) : (
                <div className="booking-agenda-groups">
                    {groups.map(([day, items]) => (
                        <section key={day} className="booking-agenda-day">
                            <h4 className="booking-agenda-daylabel">{day}</h4>
                            <ul className="booking-agenda-list">
                                {items.map(booking => {
                                    const tz = booking.timezone || booking.resource?.timezone;
                                    const tone = BOOKING_STATUS_TONE[booking.status] || 'muted';
                                    return (
                                        <li key={booking.id}>
                                            <button
                                                type="button"
                                                className="booking-agenda-row"
                                                onClick={() => setSelected(booking)}
                                            >
                                                <span className="booking-agenda-time">
                                                    {formatTimeOnly(booking.startAt, tz)}
                                                </span>
                                                <span
                                                    className="booking-agenda-bar"
                                                    style={{ backgroundColor: booking.resource?.color || 'var(--bk-accent)' }}
                                                    aria-hidden="true"
                                                />
                                                <span className="booking-agenda-info">
                                                    <span className="booking-agenda-title">
                                                        {booking.product?.name || bookingCustomerLabel(booking)}
                                                    </span>
                                                    <span className="booking-agenda-sub">
                                                        {booking.resource?.name || `Calendar #${booking.resourceId}`}
                                                        {booking.product && ` · ${bookingCustomerLabel(booking)}`}
                                                    </span>
                                                </span>
                                                <span className="booking-agenda-end">
                                                    {booking.pricing?.total !== undefined && (
                                                        <span className="booking-agenda-price">
                                                            {formatCents(booking.pricing.total, booking.pricing.currency)}
                                                        </span>
                                                    )}
                                                    <span className={`booking-badge ${tone}`}>
                                                        {BOOKING_STATUS_LABELS[booking.status] || booking.status}
                                                    </span>
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                    ))}
                </div>
            )}

            {selected && (
                <BookingDetailModal
                    booking={selected}
                    onUpdated={handleUpdated}
                    onClose={() => setSelected(null)}
                />
            )}
        </div>
    );
}
